import { useEffect, useState } from "react";

async function api(path, options) {
  return fetch(path, { credentials: "same-origin", headers: { "Content-Type": "application/json" }, ...options });
}

const STATUS_LABEL = { yes: "Going", maybe: "Maybe", no: "Declined" };

function CheckinLink({ eventId }) {
  const [link, setLink] = useState(null);
  const [visible, setVisible] = useState(false);

  async function show() {
    if (!link) {
      const res = await api(`/api/events/${eventId}/checkin-link`);
      if (res.ok) setLink(await res.json());
    }
    setVisible(true);
  }

  if (!visible) {
    return (
      <button className="btn-ghost" type="button" onClick={show}>
        Show check-in QR
      </button>
    );
  }
  if (!link) return <span className="muted">Loading…</span>;
  return (
    <div className="form-card" style={{ marginTop: 8, textAlign: "center" }}>
      <div
        style={{ width: 220, margin: "0 auto" }}
        dangerouslySetInnerHTML={{ __html: link.qr_svg }}
      />
      <p className="muted" style={{ fontSize: 13, wordBreak: "break-all", margin: "8px 0 4px" }}>
        {link.url}
      </p>
      <p className="muted" style={{ fontSize: 12, margin: 0 }}>
        Display this at the door — attendees scan it and check themselves in.
      </p>
      <button className="btn-ghost" style={{ marginTop: 8 }} type="button" onClick={() => setVisible(false)}>
        Hide
      </button>
    </div>
  );
}

export function EventRsvps({ item, canManage }) {
  const [data, setData] = useState(null);
  const [form, setForm] = useState({ name: "", email: "", status: "yes", dietary_requirements: "" });
  const [adding, setAdding] = useState(false);

  function load() {
    api(`/api/events/${item.id}/rsvps`)
      .then((res) => (res.ok ? res.json() : { rsvps: [], summary: null }))
      .then(setData);
  }
  useEffect(load, [item.id]);

  async function addAttendee(e) {
    e.preventDefault();
    if (!form.name.trim()) return;
    await api(`/api/events/${item.id}/rsvps`, { method: "POST", body: JSON.stringify(form) });
    setForm({ name: "", email: "", status: "yes", dietary_requirements: "" });
    setAdding(false);
    load();
  }
  async function remove(r) {
    await api(`/api/rsvps/${r.id}`, { method: "DELETE" });
    load();
  }
  async function setCheckedIn(r, checkedIn) {
    await api(`/api/rsvps/${r.id}/checkin`, { method: "POST", body: JSON.stringify({ checked_in: checkedIn }) });
    load();
  }

  if (data === null) return <p className="muted">Loading RSVPs…</p>;
  const { rsvps, summary } = data;
  const checkedIn = rsvps.filter((r) => r.checked_in_at).length;

  return (
    <div style={{ padding: "4px 2px" }}>
      <div style={{ display: "flex", gap: 16, flexWrap: "wrap", alignItems: "center", marginBottom: 12 }}>
        <span><strong>{summary.going}</strong> going</span>
        {(summary.online > 0 || summary.in_person > 0) && (
          <span className="muted">{summary.in_person} in person · {summary.online} online</span>
        )}
        <span className="muted">{summary.maybe} maybe · {summary.declined} declined</span>
        <span><strong>{checkedIn}</strong> checked in</span>
        {canManage && (
          <span style={{ marginLeft: "auto", display: "flex", gap: 8 }}>
            <CheckinLink eventId={item.id} />
            {!adding && (
              <button className="btn-ghost" type="button" onClick={() => setAdding(true)}>
                Add attendee
              </button>
            )}
          </span>
        )}
      </div>

      {adding && (
        <form className="form-card" onSubmit={addAttendee} style={{ marginBottom: 12 }}>
          <div className="form-grid">
            <label className="field">Name<input name="name" required value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} /></label>
            <label className="field">Email<input type="email" name="email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} /></label>
            <label className="field">Status
              <select name="status" value={form.status} onChange={(e) => setForm({ ...form, status: e.target.value })}>
                <option value="yes">Going</option><option value="maybe">Maybe</option><option value="no">Declined</option>
              </select>
            </label>
            <label className="field" style={{ gridColumn: "1 / -1" }}>Dietary<input name="dietary" value={form.dietary_requirements} onChange={(e) => setForm({ ...form, dietary_requirements: e.target.value })} /></label>
          </div>
          <div className="field-actions">
            <button className="btn-primary" style={{ width: "auto" }} type="submit">Add</button>
            <button className="btn-ghost" type="button" onClick={() => setAdding(false)}>Cancel</button>
          </div>
        </form>
      )}

      {summary.dietary.length > 0 && (
        <div className="notice" style={{ marginBottom: 12 }}>
          <strong>Catering — dietary requirements ({summary.dietary.length}):</strong>
          <ul style={{ margin: "6px 0 0", paddingLeft: 18 }}>
            {summary.dietary.map((d, i) => (
              <li key={i}>{d.name}: {d.dietary}</li>
            ))}
          </ul>
        </div>
      )}

      {rsvps.length ? (
        <table className="tbl">
          <thead><tr><th>Name</th><th>Status</th><th>Dietary</th><th>Attendance</th>{canManage && <th />}</tr></thead>
          <tbody>
            {rsvps.map((r) => (
              <tr key={r.id}>
                <td>{r.name}{r.email && <><br /><span className="muted" style={{ fontSize: 12 }}>{r.email}</span></>}</td>
                <td>{STATUS_LABEL[r.status] || r.status}</td>
                <td>{r.dietary_requirements || "—"}</td>
                <td>
                  {r.checked_in_at ? (
                    <span style={{ color: "var(--green)", fontWeight: 600 }}>
                      ✓ {r.checked_in_at.slice(11, 16)}
                    </span>
                  ) : (
                    <span className="muted">—</span>
                  )}
                </td>
                {canManage && (
                  <td className="row-actions">
                    {r.checked_in_at ? (
                      <button className="btn-ghost" type="button" onClick={() => setCheckedIn(r, false)}>Undo check-in</button>
                    ) : (
                      <button className="btn-ghost" type="button" onClick={() => setCheckedIn(r, true)}>Check in</button>
                    )}
                    <button className="btn-danger" type="button" onClick={() => remove(r)}>Remove</button>
                  </td>
                )}
              </tr>
            ))}
          </tbody>
        </table>
      ) : (
        <p className="empty">No RSVPs yet.</p>
      )}
    </div>
  );
}
